import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DollarSign, TrendingUp, TrendingDown, ArrowRight } from 'lucide-react';
import { dashboardService } from '../../services/dashboardService';

const RevenueWidget = ({ viewType }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await dashboardService.getRevenueSummary(viewType);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching revenue:', error);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [viewType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
  };

  const TrendIndicator = ({ value }) => {
    if (value === 0) return null;
    const isPositive = value > 0;
    return (
      <span className={`inline-flex items-center text-xs font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
        {isPositive ? <TrendingUp className="h-3 w-3 mr-0.5" /> : <TrendingDown className="h-3 w-3 mr-0.5" />}
        {Math.abs(value)}%
      </span>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow border p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4 w-40"></div>
          <div className="space-y-3">
            <div className="h-16 bg-gray-100 rounded"></div>
            <div className="h-16 bg-gray-100 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow border">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <DollarSign className="h-5 w-5 text-green-500 mr-2" />
            Revenue Summary
          </h3>
          <button
            onClick={() => navigate('/billing')}
            className="text-sm text-yellow-600 hover:text-yellow-800 font-medium flex items-center"
          >
            Billing <ArrowRight className="h-3 w-3 ml-1" />
          </button>
        </div>
      </div>

      <div className="p-6">
        {!data ? (
          <div className="text-center py-6">
            <div className="text-gray-400 mb-3">
              <DollarSign className="h-12 w-12 mx-auto" />
            </div>
            <p className="text-gray-600">Unable to load revenue data</p>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Billed vs Collected this month */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <div className="text-xs font-medium text-blue-600 uppercase tracking-wide">Billed This Month</div>
                <div className="text-xl font-bold text-gray-900 mt-1">{formatCurrency(data.billed_this_month)}</div>
                <TrendIndicator value={data.billed_change_pct} />
              </div>
              <div className="p-3 bg-green-50 rounded-lg">
                <div className="text-xs font-medium text-green-600 uppercase tracking-wide">Collected This Month</div>
                <div className="text-xl font-bold text-gray-900 mt-1">{formatCurrency(data.collected_this_month)}</div>
                <TrendIndicator value={data.collected_change_pct} />
              </div>
            </div>

            {/* Collection Rate */}
            {data.billed_this_month > 0 && (
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm text-gray-600">Collection Rate</span>
                  <span className="text-sm font-semibold text-gray-900">{data.collection_rate}%</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-500 ${
                      data.collection_rate >= 80 ? 'bg-green-500' :
                      data.collection_rate >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${Math.min(data.collection_rate, 100)}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Outstanding & Last Month */}
            <div className="space-y-2 pt-2 border-t border-gray-100">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total Outstanding</span>
                <span className="text-sm font-semibold text-orange-600">{formatCurrency(data.total_outstanding)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Collected Last Month</span>
                <span className="text-sm font-medium text-gray-700">{formatCurrency(data.collected_last_month)}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RevenueWidget;
